"use client";

import Decimal from "decimal.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIdentity } from "@/contexts/IdentityContext";
import { useBtcPrice } from "@/hooks/useBtcPrice";
import { logger } from "@/lib/logger";
import {
	VIP_GOLD_USD_THRESHOLD,
	VIP_SILVER_USD_THRESHOLD,
} from "@/lib/vip/config";
import { vipCacheManager } from "@/utils/cache-manager";

export interface BabyOdinBalanceData {
	lpTokenBalanceRaw: string;
	lpBtcNanoSats: string;
	lpBtcAmount: number;
	lpUsdValue: number;
	isEligible: boolean;
	isSilverEligible: boolean;
	isLoading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
}

export interface UseBabyOdinBalanceOptions {
	identityId?: string;
	enabled?: boolean;
}

const BABY_ODIN_TOKEN_ID = "2rfi";

const CACHE_KEY_BABY_ODIN_LP = "baby_odin_lp_balance";
const CACHE_TTL = 5 * 60 * 1000; // 5分钟缓存

const log = logger.child({ module: "hooks/useBabyOdinBalance" });

const retryApiCall = async (
	apiCall: () => Promise<Response>,
	signal?: AbortSignal,
	maxRetries = 3,
	delay = 1000,
): Promise<Response> => {
	for (let i = 0; i < maxRetries; i++) {
		try {
			if (signal?.aborted) {
				throw new DOMException("Aborted", "AbortError");
			}
			const response = await apiCall();
			if (response.ok) return response;
			throw new Error(`HTTP ${response.status}: ${response.statusText}`);
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") {
				throw error;
			}
			log.warn("🔄 API调用失败", {
				event: "vip_api_retry",
				data: { attempt: i + 1, maxRetries },
				error,
			});
			if (i === maxRetries - 1) throw error;
			await new Promise((resolve) => setTimeout(resolve, delay * (i + 1))); // 递增延迟
		}
	}
	throw new Error("所有重试都失败了");
};

export function useBabyOdinBalance(
	options: UseBabyOdinBalanceOptions = {},
): BabyOdinBalanceData {
	const [lpTokenBalanceRaw, setLpTokenBalanceRaw] = useState("0");
	const [lpBtcNanoSats, setLpBtcNanoSats] = useState("0");
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { principal, isAuthenticated } = useIdentity();
	const { btcPrice } = useBtcPrice();
	const abortControllerRef = useRef<AbortController | null>(null);
	const targetIdentityId = options.identityId ?? principal ?? null;
	const enabled = options.enabled ?? !!(options.identityId || isAuthenticated);

	const fetchBalance = useCallback(async (forceRefresh = false) => {
		if (!enabled || !targetIdentityId) {
			setLpTokenBalanceRaw("0");
			setLpBtcNanoSats("0");
			setError(null);
			return;
		}

		abortControllerRef.current?.abort();
		const controller = new AbortController();
		abortControllerRef.current = controller;

		setIsLoading(true);
		setError(null);

		try {
			log.info("🚀 [VIP] 开始获取 LP 持仓数据", {
				event: "vip_balance_flow_start",
				data: { targetIdentityId, time: new Date().toLocaleString() },
			});

			const cacheKey = `${CACHE_KEY_BABY_ODIN_LP}_${targetIdentityId}`;
			if (!forceRefresh) {
				const cachedBalance = vipCacheManager.get(cacheKey) as
					| { lpTokenBalanceRaw: string; lpBtcNanoSats: string }
					| null;
				if (cachedBalance !== null) {
					setLpTokenBalanceRaw(cachedBalance.lpTokenBalanceRaw);
					setLpBtcNanoSats(cachedBalance.lpBtcNanoSats);
					setError(null);
					return;
				}
			}

			const lpResponse = await retryApiCall(
				() =>
					fetch(
						`https://api.odin.fun/v1/user/${targetIdentityId}/liquidity?id_in=${BABY_ODIN_TOKEN_ID}`,
						{ signal: controller.signal },
					),
				controller.signal,
			);

			const lpResponseData = await lpResponse.json();

			log.debug("✅ [VIP] LP API 数据获取成功", {
				event: "vip_balance_api_success",
				data: {
					lpResponseData,
				},
			});

			let nextLpTokenBalanceRaw = "0";
			let nextLpBtcNanoSats = "0";
			if (lpResponseData.data && lpResponseData.data.length > 0) {
				const userLiquidity = lpResponseData.data.find(
					(item: any) => item.token.id === BABY_ODIN_TOKEN_ID,
				);
				if (userLiquidity) {
					const lpTokenBalance = userLiquidity.balance?.toString() || "0";
					const poolBtcLiquidity = userLiquidity.token?.user_btc_liquidity ?? 0;
					const totalLpTokens = userLiquidity.token?.user_lp_tokens ?? 0;

					nextLpTokenBalanceRaw = lpTokenBalance;
					if (
						lpTokenBalance !== "0" &&
						new Decimal(totalLpTokens).gt(0) &&
						new Decimal(poolBtcLiquidity).gt(0)
					) {
						nextLpBtcNanoSats = new Decimal(lpTokenBalance)
							.mul(poolBtcLiquidity)
							.div(totalLpTokens)
							.toDecimalPlaces(0, Decimal.ROUND_FLOOR)
							.toFixed(0);
					}
				}
			}

			vipCacheManager.set(
				cacheKey,
				{
					lpTokenBalanceRaw: nextLpTokenBalanceRaw,
					lpBtcNanoSats: nextLpBtcNanoSats,
				},
				CACHE_TTL,
			);
			setLpTokenBalanceRaw(nextLpTokenBalanceRaw);
			setLpBtcNanoSats(nextLpBtcNanoSats);
		} catch (err) {
			if (err instanceof DOMException && err.name === "AbortError") {
				return;
			}
			log.error("❌ [VIP] 获取持仓数据失败", {
				event: "vip_balance_flow_error",
				error: err,
			});
			setError("获取 LP 余额失败");
			setLpTokenBalanceRaw("0");
			setLpBtcNanoSats("0");
		} finally {
			if (!controller.signal.aborted) {
				setIsLoading(false);
			}
		}
	}, [enabled, targetIdentityId]);

	const lpBtcAmount = new Decimal(lpBtcNanoSats).div(1e11).toNumber();
	const lpUsdValue = new Decimal(lpBtcAmount)
		.mul(2)
		.mul(btcPrice || 0)
		.toNumber();
	const isEligible = lpUsdValue >= VIP_GOLD_USD_THRESHOLD;
	const isSilverEligible = lpUsdValue >= VIP_SILVER_USD_THRESHOLD;

	const forceRefresh = useCallback(async () => {
		if (targetIdentityId) {
			const cacheKey = `${CACHE_KEY_BABY_ODIN_LP}_${targetIdentityId}`;
			vipCacheManager.delete(cacheKey);
			log.info("🗑️ [VIP] 已清除 LP 缓存，强制刷新数据", {
				event: "vip_balance_cache_cleared",
				data: { targetIdentityId },
			});
		}
		await fetchBalance(true);
	}, [targetIdentityId, fetchBalance]);

	useEffect(() => {
		void fetchBalance();
		return () => {
			abortControllerRef.current?.abort();
		};
	}, [fetchBalance]);

	return {
		lpTokenBalanceRaw,
		lpBtcNanoSats,
		lpBtcAmount,
		lpUsdValue,
		isEligible,
		isSilverEligible,
		isLoading,
		error,
		refresh: forceRefresh,
	};
}

export default useBabyOdinBalance;
